import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import "../letterstyles/EmployeeIdCard.css";

const EmployeeIdCard = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const user = state?.user;
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  const [company, setCompany] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!user) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details:", err);
      }
    };
    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/list-employee-kyc?userId=${user?.userId}&superAdminId=${superAdminId}`
        );
        const data = await res.json();
        if (res.ok && data.documents?.length > 0) {
          const photoDoc = data.documents.find(
            (doc) =>
              doc.docName?.toLowerCase() === "photo" &&
              doc.status?.toLowerCase() === "approved"
          );
          if (photoDoc?.docImage) {
            setPhotoUrl(`https://payyug.in/uploads/${photoDoc.docImage}`);
          }
        }
      } catch (err) {
        console.error("Failed to fetch employee photo:", err);
      }
    };
    if (user?.userId) fetchPhoto();
  }, [user]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleDownload = () => {
    if (!cardRef.current) return;

    const cardEl = cardRef.current;

    // Temporarily fix width so html2canvas captures exact card width
    cardEl.style.width = "208px"; // 55mm @ 96dpi

    const opt = {
      margin: 0,
      filename: `${user?.fullName || "Employee"}_ID_Card.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        scrollY: 0,
        scrollX: 0,
        width: 208,           // ✅ fixed capture width = exact card width, no drift
        windowWidth: 208,
        logging: false,
      },
      jsPDF: {
        unit: "mm",
        format: [63, 115],    // ✅ fixed: wide enough, tall enough, centered
        orientation: "portrait",
        putOnlyUsedFonts: true,
      },
    };

    html2pdf()
      .set(opt)
      .from(cardEl)
      .save()
      .then(() => {
        // Restore responsive width after capture
        cardEl.style.width = "";
      });
  };

  if (!user) return null;

  return (
    <div className="idcard-wrapper">
      <div className="idcard-actions no-print">
        <button className="idcard-btn" onClick={handleDownload}>
          ⬇️ Download
        </button>
      </div>

      <div className="idcard vertical" ref={cardRef}>

        {/* Header */}
        <div
          className="idcard-header-rect"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {company?.logoUrl ? (
            <img
              src={company.logoUrl}
              alt="Company Logo"
              className="idcard-logo-rect"
              crossOrigin="anonymous"
              style={{
                width: "55px",
                height: "55px",
                objectFit: "contain",
                borderRadius: "50%",
              }}
            />
          ) : (
            <div className="idcard-logo-placeholder" />
          )}
          <span className="idcard-company-name" style={{ fontSize: "14px", fontWeight: "700" }}>
            {company?.name || "Company Name"}
          </span>
          <span className="idcard-company-tagline">
            {company?.address || ""}
          </span>
          <span className="idcard-company-tagline">
            {company?.domain || ""}
          </span>
        </div>

        {/* Photo */}
        <div className="idcard-photo-section">
          <div className="idcard-photo-curve" />
          <img
            src={photoUrl || "https://via.placeholder.com/120.png?text=Photo"}
            alt="Employee"
            className="idcard-photo-circle"
            crossOrigin="anonymous"
          />
        </div>

        {/* Name + Role */}
        <div className="idcard-employee-info">
          <h2 className="idcard-emp-name">{user.fullName}</h2>
          <h3 className="idcard-emp-role">{user.roleName}</h3>
        </div>

        {/* Details */}
        <div className="idcard-details-vertical">
          <div className="row">
            <span>EMPLOYEE ID</span>
            <span>{user.userId || "--"}</span>
          </div>
          <div className="row">
            <span>PHONE</span>
            <span>{user.mobileNumber || "--"}</span>
          </div>
          <div className="row">
            <span>DATE OF JOINING</span>
            <span>{formatDate(user.dateOfJoining)}</span>
          </div>
        </div>

        {/* Footer — single line, no <br/> */}
        <div className="idcard-footer-rect">
          <div className="idcard-footer-contact">
            {/* ✅ No <br/> — white-space:nowrap in CSS keeps each on one line */}
            <span>Email: {company?.email || "email@company.com"}</span>
            <span>Ph: +91 {company?.contact || "XXXXXXXXXX"}</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmployeeIdCard;